import { useMemo, useState } from "react";

/**
 * The first `limit` items, plus the selected one when it lies beyond them, so a selection made
 * elsewhere (deep link, saved form) never disappears from the list. It sits at the end, out of sort
 * order, until "Show more" reaches its real position.
 */
export function takeVisible<T extends { id: string }>(
  items: readonly T[],
  limit: number,
  selectedId: string | null | undefined,
): readonly T[] {
  if (items.length <= limit) return items;
  const head = items.slice(0, limit);
  if (!selectedId || head.some(item => item.id === selectedId)) return head;
  const selected = items.find(item => item.id === selectedId);
  return selected ? [...head, selected] : head;
}

/**
 * Renders a long list page by page instead of all at once (no virtualization library in the repo).
 * Reset the page count by remounting the caller with a new `key` (e.g. the search text).
 */
export function useShowMore<T extends { id: string }>(
  items: readonly T[],
  pageSize: number,
  selectedId: string | null | undefined,
) {
  const [limit, setLimit] = useState(pageSize);
  const visible = useMemo(() => takeVisible(items, limit, selectedId), [items, limit, selectedId]);
  return {
    visible,
    hiddenCount: items.length - visible.length,
    showMore: () => setLimit(current => current + pageSize),
  };
}
